import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Send, User2, Mail, Phone, Globe, Users, TrendingUp,
  Megaphone, CheckCircle2, XCircle, Loader2, Sparkles, ShieldCheck,
  AlertCircle, DollarSign, Wallet, Trophy, Zap,
} from "lucide-react";
import { api } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

const TRAFFIC_SOURCES = [
  "Telegram", "Facebook", "Instagram", "YouTube", "TikTok",
  "Website/Blog", "WhatsApp", "Offline", "Other",
];

const PERKS = [
  { icon: Trophy, color: "#fbbf24", title: "Up to 30%", sub: "Net-loss commission" },
  { icon: Wallet, color: "#22c55e", title: "5 Methods", sub: "Crypto + Local payouts" },
  { icon: Zap, color: "#3b82f6", title: "Every 15d", sub: "Auto payout cycle" },
  { icon: ShieldCheck, color: "#a855f7", title: "2FA Panel", sub: "Secure dedicated dashboard" },
];

export default function AffiliateApplyPage() {
  const [form, setForm] = useState({
    panel_username: "",
    email: "",
    phone: "",
    website: "",
    social_handles: "",
    traffic_source: "Telegram",
    monthly_traffic: "",
    promo_plan: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [unameStatus, setUnameStatus] = useState({ checking: false, available: null, reason: "" });
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ---- Live username availability check (debounced) ----
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const raw = (form.panel_username || "").trim().toLowerCase();
    if (!raw) {
      setUnameStatus({ checking: false, available: null, reason: "" });
      return;
    }
    // Client-side format check first
    if (!/^[a-z0-9_]{4,20}$/.test(raw)) {
      setUnameStatus({
        checking: false,
        available: false,
        reason: "4-20 chars · lowercase a-z, 0-9, _ only",
      });
      return;
    }
    if (raw.startsWith("_") || raw.endsWith("_")) {
      setUnameStatus({ checking: false, available: false, reason: "No leading/trailing underscores" });
      return;
    }
    setUnameStatus({ checking: true, available: null, reason: "Checking…" });
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/profile/affiliate/check-username`, { params: { u: raw } });
        setUnameStatus({ checking: false, available: data.available, reason: data.reason || "" });
      } catch {
        setUnameStatus({ checking: false, available: null, reason: "Could not check — try again" });
      }
    }, 450);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [form.panel_username]);

  // ---- Progress calculation ----
  const progress = useMemo(() => {
    const required = [
      !!form.panel_username && unameStatus.available === true,
      !!form.email,
      !!form.traffic_source,
      !!(form.promo_plan && form.promo_plan.length >= 20),
    ];
    const done = required.filter(Boolean).length;
    return Math.round((done / required.length) * 100);
  }, [form, unameStatus]);

  const canSubmit =
    unameStatus.available === true &&
    !!form.email &&
    !!form.promo_plan &&
    form.promo_plan.length >= 20 &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.panel_username) return toast.error("Choose a panel username");
    if (unameStatus.available !== true) return toast.error("Pick an available username");
    if (!form.email) return toast.error("Email is required");
    if (!form.promo_plan || form.promo_plan.length < 20)
      return toast.error("Promotion plan must be at least 20 characters");
    setSubmitting(true);
    try {
      await api.post("/profile/affiliate/apply", {
        ...form,
        panel_username: form.panel_username.trim().toLowerCase(),
      });
      toast.success("Application submitted! We'll review within 48 hours.");
      navigate("/profile/affiliate");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ProfileShell title="Apply for Affiliate">
      <form onSubmit={submit} className="pb-10 space-y-4" data-testid="affiliate-apply-form">
        {/* Back */}
        <button
          type="button"
          onClick={() => navigate("/profile/affiliate")}
          className="inline-flex items-center gap-1.5 text-white/65 hover:text-white text-sm"
          data-testid="apply-back"
        >
          <ArrowLeft size={14} /> Back to Affiliate
        </button>

        {/* Hero */}
        <div
          className="rounded-2xl p-5 relative overflow-hidden"
          style={{
            background:
              "radial-gradient(800px 400px at 90% 0%, rgba(251,191,36,0.20), transparent 60%), linear-gradient(135deg, rgba(34,192,115,0.18), rgba(13,107,58,0.05))",
            border: "1px solid rgba(34,192,115,0.45)",
          }}
        >
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-[10px] font-bold uppercase tracking-[0.22em] mb-3">
            <Sparkles size={11} /> Apply for Affiliate
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">
            Join the <span style={{ background: "linear-gradient(90deg,#22c073,#fbbf24)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Maxlive88</span> Partner Program
          </h1>
          <p className="text-sm text-white/70 mt-2 max-w-md">
            Tell us about your audience and how you'll promote. Approval typically within 24-48 hours.
          </p>
          {/* Perks row */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PERKS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="rounded-lg p-2.5 bg-black/35 border border-white/10">
                  <Icon size={14} style={{ color: p.color }} className="mb-1" />
                  <div className="text-white font-bold text-[12px]">{p.title}</div>
                  <div className="text-white/50 text-[10px]">{p.sub}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Progress strip */}
        <div className="rounded-xl bg-black/35 border border-white/10 p-3 flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-widest text-white/55 font-semibold shrink-0">Form Progress</div>
          <div className="flex-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: `${progress}%`,
                background: progress === 100 ? "linear-gradient(90deg, #22c55e, #fbbf24)" : "linear-gradient(90deg, #3b82f6, #22c073)",
              }}
            />
          </div>
          <div className={`text-[12px] font-bold tabular-nums ${progress === 100 ? "text-emerald-300" : "text-white/80"}`}>{progress}%</div>
        </div>

        {/* ============ SECTION 1: Account ============ */}
        <Section
          step={1}
          title="Account & Login"
          subtitle="Choose how you'll sign in to your Promo Panel"
          icon={User2}
          color="#22c55e"
        >
          {/* Username with live availability */}
          <Field
            label="Promo Panel Username *"
            hint="This is what you'll use to log in at maxlive88.com/promo — choose carefully, it can't be changed easily"
          >
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/40 text-sm font-mono select-none">
                @
              </div>
              <input
                type="text"
                value={form.panel_username}
                onChange={(e) => set("panel_username", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                placeholder="yourname"
                maxLength={20}
                autoComplete="off"
                className={`w-full pl-9 pr-12 h-12 rounded-xl bg-black/45 border-2 outline-none text-white text-sm font-mono transition ${
                  unameStatus.available === true
                    ? "border-emerald-400/70 focus:border-emerald-400"
                    : unameStatus.available === false
                    ? "border-rose-400/60 focus:border-rose-400"
                    : "border-white/10 focus:border-emerald-400/50"
                }`}
                data-testid="apply-username"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {unameStatus.checking ? (
                  <Loader2 size={16} className="animate-spin text-white/50" />
                ) : unameStatus.available === true ? (
                  <CheckCircle2 size={18} className="text-emerald-400" />
                ) : unameStatus.available === false ? (
                  <XCircle size={18} className="text-rose-400" />
                ) : null}
              </div>
            </div>
            {form.panel_username && unameStatus.reason && (
              <div className={`mt-1.5 text-[11px] flex items-center gap-1 ${
                unameStatus.available === true ? "text-emerald-300" :
                unameStatus.available === false ? "text-rose-300" : "text-white/50"
              }`}>
                {unameStatus.available === true && <CheckCircle2 size={11} />}
                {unameStatus.available === false && <AlertCircle size={11} />}
                {unameStatus.reason}
              </div>
            )}
            {form.panel_username && unameStatus.available === true && (
              <div className="mt-2 text-[10px] text-white/45">
                Your login: <span className="font-mono text-emerald-300">{form.panel_username.trim().toLowerCase()}</span>
              </div>
            )}
          </Field>
        </Section>

        {/* ============ SECTION 2: Contact ============ */}
        <Section
          step={2}
          title="Contact Information"
          subtitle="We'll send your panel credentials here after approval"
          icon={Mail}
          color="#3b82f6"
        >
          <Field label="Email *" hint="Credentials will be emailed to this address">
            <IconInput
              icon={Mail}
              type="email"
              value={form.email}
              onChange={(v) => set("email", v)}
              placeholder="you@example.com"
              testId="apply-email"
            />
          </Field>
          <Field label="Phone / WhatsApp" hint="Optional — for support contact">
            <IconInput
              icon={Phone}
              type="tel"
              value={form.phone}
              onChange={(v) => set("phone", v)}
              placeholder="+8801XXXXXXXXX"
              testId="apply-phone"
            />
          </Field>
        </Section>

        {/* ============ SECTION 3: Audience ============ */}
        <Section
          step={3}
          title="Your Audience"
          subtitle="Tell us where you'll be promoting"
          icon={Users}
          color="#a855f7"
        >
          <Field label="Website / Telegram / Channel URL" hint="Where do you reach your audience?">
            <IconInput
              icon={Globe}
              type="url"
              value={form.website}
              onChange={(v) => set("website", v)}
              placeholder="https://t.me/yourchannel"
              testId="apply-website"
            />
          </Field>
          <Field label="Social Media Handles" hint="Instagram / Facebook / TikTok handles">
            <IconInput
              icon={Users}
              type="text"
              value={form.social_handles}
              onChange={(v) => set("social_handles", v)}
              placeholder="@instagram, fb.com/page, @tiktok"
              testId="apply-social"
            />
          </Field>
          <Field label="Main Traffic Source *">
            <select
              value={form.traffic_source}
              onChange={(e) => set("traffic_source", e.target.value)}
              className="w-full h-12 px-4 rounded-xl bg-black/45 border-2 border-white/10 focus:border-emerald-400/50 outline-none text-white text-sm"
              data-testid="apply-traffic-source"
            >
              {TRAFFIC_SOURCES.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
            </select>
          </Field>
          <Field label="Monthly Reach (Users / Views)" hint="Approximate audience size">
            <IconInput
              icon={TrendingUp}
              type="text"
              value={form.monthly_traffic}
              onChange={(v) => set("monthly_traffic", v)}
              placeholder="e.g. 5,000"
              testId="apply-monthly"
            />
          </Field>
        </Section>

        {/* ============ SECTION 4: Plan ============ */}
        <Section
          step={4}
          title="Promotion Strategy"
          subtitle="How exactly will you promote Maxlive88?"
          icon={Megaphone}
          color="#fbbf24"
        >
          <Field
            label="Promotion Plan *"
            hint={`${form.promo_plan.length} / 500 characters · minimum 20 characters`}
          >
            <textarea
              value={form.promo_plan}
              onChange={(e) => set("promo_plan", e.target.value.slice(0, 500))}
              rows={5}
              placeholder="I'll promote via Telegram channel of 5k+ active users in the Bangladesh sports betting niche. Daily promo code posts, exclusive bonus highlights, and weekend tournaments..."
              className={`w-full p-4 rounded-xl bg-black/45 border-2 outline-none text-white text-sm leading-relaxed transition ${
                form.promo_plan.length >= 20
                  ? "border-emerald-400/30 focus:border-emerald-400/60"
                  : "border-white/10 focus:border-emerald-400/50"
              }`}
              data-testid="apply-promo-plan"
            />
          </Field>
        </Section>

        {/* What happens next */}
        <div className="rounded-2xl bg-black/30 border border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-widest text-white/55 font-semibold mb-3 flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-emerald-300" /> What happens after you submit
          </div>
          <div className="space-y-2.5">
            {[
              { n: 1, t: "We review your application within 24-48 hours" },
              { n: 2, t: "On approval, your Promo Panel credentials are sent to your email" },
              { n: 3, t: "Log in at maxlive88.com/promo → change password → optionally enable 2FA" },
              { n: 4, t: "Start sharing your promo code & receive commissions every 15 days" },
            ].map((s) => (
              <div key={s.n} className="flex items-start gap-2.5 text-[12px] text-white/80">
                <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-bold text-[10px]">{s.n}</span>
                <span>{s.t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full h-14 rounded-2xl text-base font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
          style={{
            background: canSubmit
              ? "linear-gradient(180deg, #22c073 0%, #159654 50%, #0d6b3a 100%)"
              : "rgba(255,255,255,0.08)",
            color: "#fff",
            boxShadow: canSubmit ? "0 12px 36px rgba(13,107,58,0.55)" : "none",
          }}
          data-testid="apply-submit"
        >
          {submitting ? (
            <><Loader2 size={18} className="animate-spin" /> Submitting…</>
          ) : (
            <><Send size={16} /> Submit Application</>
          )}
        </button>

        <p className="text-[11px] text-white/45 text-center">
          By submitting you agree to the affiliate Terms of Service. Your account will be reviewed by our team.
        </p>
      </form>
    </ProfileShell>
  );
}

/* ============================================================
 *  Reusable section + field primitives
 * ============================================================ */
function Section({ step, title, subtitle, icon: Icon, color, children }) {
  return (
    <div className="rounded-2xl bg-black/30 border border-white/10 p-4 sm:p-5">
      <div className="flex items-start gap-3 mb-4">
        <div
          className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-black text-[12px]"
          style={{
            background: `linear-gradient(135deg, ${color}30, ${color}10)`,
            border: `1px solid ${color}50`,
            color,
          }}
        >
          {step}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {Icon && <Icon size={14} style={{ color }} />}
            <h3 className="text-base sm:text-lg font-black text-white">{title}</h3>
          </div>
          <p className="text-[12px] text-white/55 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-white/45 mt-1.5">{hint}</div>}
    </div>
  );
}

function IconInput({ icon: Icon, type = "text", value, onChange, placeholder, testId }) {
  return (
    <div className="relative">
      {Icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/40">
          <Icon size={15} />
        </div>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-4 h-12 rounded-xl bg-black/45 border-2 border-white/10 focus:border-emerald-400/50 outline-none text-white text-sm transition`}
        data-testid={testId}
      />
    </div>
  );
}
